'use client';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as React from 'react';

import { cn } from '@/lib/ui-kit/utils';

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

// Radix tooltips only open on hover/focus and deliberately ignore touch, so on
// mobile they never open. We mirror Radix's open state and expose a setter so
// the trigger can also toggle it on tap — desktop hover keeps working because
// Radix still drives `onOpenChange`.
const TooltipOpenContext = React.createContext<{
  open: boolean;
  setOpen: (open: boolean) => void;
} | null>(null);

function Tooltip({
  open: openProp,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen ?? false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(next);
      }
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  return (
    <TooltipProvider>
      <TooltipOpenContext.Provider value={{ open, setOpen: handleOpenChange }}>
        <TooltipPrimitive.Root
          data-slot="tooltip"
          open={open}
          onOpenChange={handleOpenChange}
          {...props}
        />
      </TooltipOpenContext.Provider>
    </TooltipProvider>
  );
}

function TooltipTrigger({
  onClick,
  onPointerDown,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  const ctx = React.useContext(TooltipOpenContext);
  // The tap in progress and whether it found the tooltip open. Radix closes it
  // on pointerdown and again on every click, so the state is read before that.
  const tapRef = React.useRef<{ wasOpen: boolean } | null>(null);

  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    onPointerDown?.(event);
    tapRef.current = event.pointerType === 'touch' ? { wasOpen: ctx?.open ?? false } : null;
  };

  // A mouse or keyboard click keeps Radix's own behaviour. A tap toggles, a
  // tick later: Radix (and an open tooltip's outside-click) close it first.
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    const tap = tapRef.current;
    tapRef.current = null;
    if (!tap || !ctx) return;
    const { setOpen } = ctx;
    setTimeout(() => setOpen(!tap.wasOpen));
  };

  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      {...props}
    />
  );
}

function TooltipContent({
  className,
  sideOffset = 0,
  collisionPadding = 8,
  children,
  showArrow = true,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & {
  showArrow?: boolean;
}) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={cn(
          `origin-(--radix-tooltip-content-transform-origin) animate-in bg-v2-surface-secondary border-v2-surface-tertiary
          text-v2-text-secondary body-small fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2
          data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2
          data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0
          data-[state=closed]:zoom-out-95 z-50 w-fit rounded-2xl border p-4`,
          className,
        )}
        {...props}
      >
        {children}
        {showArrow && <TooltipPrimitive.Arrow className="fill-v2-surface-secondary" />}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
